import React from 'react';
import { useT } from '../../i18n';

// The rebalance view: where each position sits inside its band, and what the
// next action would be.
//
// The band is drawn rather than tabulated. "-27% relative against a ±30% band"
// is a sentence you have to work out; a marker sitting just inside a bracket is
// something you see. The numbers stay alongside for when the exact figure
// matters.

/**
 * The Next Action sentence, composed from the action code and the numbers the
 * API sends alongside it.
 *
 * `actionDetail` is the API's own English sentence and is the fallback — an
 * action code added to the backend before a string exists here still reads,
 * rather than showing `action.some_new_code`.
 */
/** Same fallback rule as the detail: an unmapped code shows the raw code
 *  rather than `action.label.whatever`. */
function actionLabel(action, t) {
    const key = `action.label.${action}`;
    const label = t(key);
    return label === key ? action : label;
}

function actionDetail(row, t) {
    const params = row.actionParams;
    if (!params) return row.actionDetail;
    const key = row.action === 'hold' && params.leaving ? 'action.hold_leaving' : `action.${row.action}`;
    const sentence = t(key, params);
    return sentence === key ? row.actionDetail : sentence;
}

/**
 * Positions the band, the target and the current weight on a shared scale.
 *
 * The scale runs to a fixed multiple of the target rather than to the widest
 * value on screen, so a row's marker means the same thing whatever else is in
 * the table.
 */
function BandBar({ row, t }) {
    if (row.action === 'sell_not_in_index' || !row.targetPct) return null;

    const SCALE = 2; // full width == twice the target weight
    const clamp = (n) => Math.max(0, Math.min(100, n));
    const toPct = (weight) => clamp((weight / (row.targetPct * SCALE)) * 100);

    const low = toPct(row.bandLowPct);
    const high = toPct(row.bandHighPct);
    const current = toPct(row.currentPct ?? 0);
    const over = (row.currentPct ?? 0) > row.targetPct;

    return (
        <span
            className="band"
            title={t('rebalance.bandTitle', { low: row.bandLowPct, high: row.bandHighPct })}
        >
            <span className="band__track" />
            <span className="band__zone" style={{ left: `${low}%`, width: `${high - low}%` }} />
            <span className="band__target" style={{ left: '50%' }} />
            <span
                className={`band__marker${row.breaching ? ' is-breaching' : ''}${over ? ' is-over' : ''}`}
                style={{ left: `${current}%` }}
            />
        </span>
    );
}

export default function RebalanceTable({ rows, baseCurrency, bands, hardCapPct }) {
    const { t, tn, fmt } = useT();
    const breaching = rows.filter((r) => r.breaching);
    const leaving = rows.filter((r) => r.leavingIndex);

    return (
        <>
            <div className="rebal__summary">
                {breaching.length === 0 ? (
                    <p className="rebal__ok">{t('rebalance.ok')}</p>
                ) : (
                    <p className="rebal__alert">
                        {tn('rebalance.breaching', breaching.length, {
                            symbols: breaching.map((r) => r.symbol).join(', '),
                        })}
                    </p>
                )}
                {leaving.length > 0 && (
                    <p className="rebal__note">
                        {tn('rebalance.leaving', leaving.length, {
                            symbols: leaving.map((r) => r.symbol).join(', '),
                        })}
                    </p>
                )}
            </div>

            <div className="positions__scroll">
                <table className="positions rebal">
                    <thead>
                        <tr>
                            <th className="is-left">#</th>
                            <th className="is-left is-wide">{t('rebalance.asset')}</th>
                            <th className="is-right">{t('rebalance.current')}</th>
                            <th className="is-right">{t('rebalance.target')}</th>
                            <th className="is-left band-col">{t('rebalance.band')}</th>
                            <th className="is-right">{t('rebalance.drift')}</th>
                            <th className="is-right">{t('rebalance.trade')}</th>
                            <th className="is-left">{t('rebalance.nextAction')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr
                                key={`${row.sleeve}-${row.symbol}`}
                                className={row.breaching ? 'is-breaching' : undefined}
                            >
                                <td className="is-left is-rank">
                                    {row.rank ?? <span className="positions__empty">—</span>}
                                </td>
                                <td className="is-left is-wide">
                                    <span className="rebal__symbol">{row.symbol}</span>
                                    {row.theme && <span className="rebal__theme">{row.theme}</span>}
                                    {row.leavingIndex && (
                                        <span className="rebal__tag">
                                            {t('rebalance.liveRank', { rank: row.liveRank ?? '20+' })}
                                        </span>
                                    )}
                                    {/* Why a name is here that the frozen
                                        constitution does not list — and why its
                                        target is the one it is. */}
                                    {row.entryRank !== null && row.entryRank !== undefined && (
                                        <span className="rebal__tag is-entrant">
                                            {t('rebalance.entered', { rank: row.entryRank })}
                                        </span>
                                    )}
                                </td>
                                <td className="is-right">
                                    {row.currentPct === null ? '—' : `${fmt.number(row.currentPct)}%`}
                                </td>
                                <td className="is-right">{fmt.number(row.targetPct)}%</td>
                                <td className="band-col">
                                    <BandBar row={row} t={t} />
                                </td>
                                {/* Drift is deliberately not coloured by sign. Being under
                                    target is not "bad" the way a loss is — only leaving the
                                    band means anything, so only a breach is coloured. */}
                                <td className={`is-right${row.breaching ? ' is-negative' : ' is-muted'}`}>
                                    {row.relativeDriftPct === null
                                        ? '—'
                                        : `${row.relativeDriftPct > 0 ? '+' : ''}${fmt.number(row.relativeDriftPct, 1)}%`}
                                </td>
                                <td className="is-right">
                                    {row.tradeValue === null ? (
                                        <span className="positions__empty">—</span>
                                    ) : (
                                        <span className={Number(row.tradeValue) < 0 ? 'is-negative' : 'is-positive'}>
                                            {Number(row.tradeValue) < 0 ? '−' : '+'}
                                            {fmt.number(Math.abs(Number(row.tradeValue)), 0)}
                                            {row.tradeShares !== null && (
                                                <span className="rebal__shares">
                                                    {' '}
                                                    {t('rebalance.shares', {
                                                        count: Math.abs(row.tradeShares),
                                                    })}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </td>
                                <td className={`is-left rebal__action is-${row.action}`}>
                                    <span className="rebal__action_label">
                                        {actionLabel(row.action, t)}
                                    </span>
                                    <span className="rebal__action_detail">
                                        {actionDetail(row, t)}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}
